import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const NotesPanel = ({ caseNumber }) => {
  const [notes, setNotes] = useState('');
  const [agendaItems, setAgendaItems] = useState([
    { id: 1, text: 'Review case background and timeline', completed: true },
    { id: 2, text: 'Discuss legal strategy options', completed: false },
    { id: 3, text: 'Explain next steps and timeline', completed: false },
    { id: 4, text: 'Address client questions and concerns', completed: false }
  ]);
  const [keyPoints, setKeyPoints] = useState([]);
  const [newKeyPoint, setNewKeyPoint] = useState('');
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // Auto-save notes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (notes.trim()) {
        setIsAutoSaving(true);
        // Simulate API call
        setTimeout(() => {
          setIsAutoSaving(false);
        }, 1000);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [notes]);

  const toggleAgendaItem = (id) => {
    setAgendaItems(prev => 
      prev.map(item => 
        item.id === id ? { ...item, completed: !item.completed } : item
      )
    );
  };

  const addKeyPoint = () => {
    if (newKeyPoint.trim()) {
      const keyPoint = {
        id: Date.now(),
        text: newKeyPoint.trim(),
        timestamp: new Date()
      };
      setKeyPoints(prev => [...prev, keyPoint]);
      setNewKeyPoint('');
    }
  };

  const removeKeyPoint = (id) => {
    setKeyPoints(prev => prev.filter(point => point.id !== id));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addKeyPoint();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-KE', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Notes Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-text-primary">Consultation Notes</h3>
          <div className="flex items-center space-x-2">
            {isAutoSaving && (
              <div className="flex items-center space-x-1 text-xs text-primary">
                <Icon name="RefreshCw" size={10} className="animate-spin" />
                <span>Saving...</span>
              </div>
            )}
            <div className="flex items-center space-x-1 text-xs text-text-secondary">
              <Icon name="FileText" size={12} />
              <span>{caseNumber}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Consultation Agenda */}
        <div className="p-4 border-b border-border">
          <h4 className="font-medium text-text-primary mb-3 flex items-center">
            <Icon name="CheckSquare" size={16} className="mr-2" />
            Consultation Agenda
          </h4>
          <div className="space-y-2">
            {agendaItems.map((item) => (
              <label
                key={item.id}
                className="flex items-center space-x-3 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  checked={item.completed}
                  onChange={() => toggleAgendaItem(item.id)}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
                <span
                  className={`text-sm transition-smooth ${
                    item.completed
                      ? 'text-text-secondary line-through' :'text-text-primary group-hover:text-primary'
                  }`}
                >
                  {item.text}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Key Points */}
        <div className="p-4 border-b border-border">
          <h4 className="font-medium text-text-primary mb-3 flex items-center">
            <Icon name="Star" size={16} className="mr-2" />
            Key Points
          </h4>
          
          {/* Add Key Point */}
          <div className="flex space-x-2 mb-3">
            <input
              type="text"
              value={newKeyPoint}
              onChange={(e) => setNewKeyPoint(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Add important point..."
              className="flex-1 px-3 py-2 text-sm border border-border rounded-interactive focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <button
              onClick={addKeyPoint}
              disabled={!newKeyPoint.trim()}
              className="px-3 py-2 bg-primary hover:bg-primary-dark disabled:bg-secondary-200 disabled:text-text-secondary text-white rounded-interactive transition-smooth"
            >
              <Icon name="Plus" size={14} />
            </button>
          </div>

          {/* Key Points List */}
          <div className="space-y-2">
            {keyPoints.map((point) => (
              <div
                key={point.id}
                className="flex items-start space-x-2 p-2 bg-secondary-50 rounded-lg group"
              >
                <Icon name="ArrowRight" size={12} className="mt-1 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary">{point.text}</p>
                  <p className="text-xs text-text-secondary mt-1">
                    {formatTime(point.timestamp)}
                  </p>
                </div>
                <button
                  onClick={() => removeKeyPoint(point.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-text-secondary hover:text-error transition-smooth"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            ))}
            {keyPoints.length === 0 && (
              <p className="text-sm text-text-secondary italic">
                No key points recorded yet
              </p>
            )}
          </div>
        </div>

        {/* Main Notes */}
        <div className="p-4">
          <h4 className="font-medium text-text-primary mb-3 flex items-center">
            <Icon name="Edit3" size={16} className="mr-2" />
            Detailed Notes
          </h4>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Record detailed consultation notes here...

• Client concerns and objectives
• Legal advice provided
• Action items and next steps
• Follow-up requirements"
            rows="12"
            className="w-full px-3 py-3 border border-border rounded-interactive resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          />
          
          <div className="flex items-center justify-between mt-2 text-xs text-text-secondary">
            <span>Notes are automatically saved and encrypted</span>
            <div className="flex items-center space-x-4">
              <span>{notes.length} characters</span>
              <div className="flex items-center space-x-1">
                <Icon name="Shield" size={10} />
                <span>Secure</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesPanel;